import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { Connection, Destination, Member, Organization } from "../common/ObjectTypes";
import DestinationDetailsModalTable from "./DestinationDetailsModalTable";
import DestinationDetailsModalConfig from "./DestinationDetailsModalConfig";
import Spinner from "../common/Spinner";
import { useScrollLock } from "../common/useScrollLock";
import { modalClasses } from "../common/tailwindClassnames";

type DestinationDetailsProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    destinationTitle: string,
    destSubPage: string | undefined,
    onSelectSubPage: Function,
    connections: Connection[],
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onToggleModal: MouseEventHandler,
    requestSort: Function,
    loadingConnections: boolean,
    onChangeSelectedDestination: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: MouseEventHandler,
    errors: any,
    rsjfErrors: any,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number
}

const DestinationDetailsModal = ({
    currentMemberIsAdmin,
    destination,
    destinationTitle,
    destSubPage,
    onSelectSubPage,
    connections,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onToggleModal,
    requestSort,
    loadingConnections,
    onChangeSelectedDestination,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    errors,
    rsjfErrors,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset
}: DestinationDetailsProps) => {
    const { t } = useTranslation();

    useScrollLock(true);

    return (
        <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 transition-colors duration-300"
                onClick={onToggleModal}></div>
            <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
                <div className="z-20 flex flex-col w-full sm:w-5/6 md:w-3/4 lg:w-2/3 xl:w-1/2 
                               h-[75vh] shadow rounded-lg bg-white dark:bg-dark-gray transition-colors duration-300">
                    {/* top portion */}
                    <div className="bg-white dark:bg-dark-gray z-30 sticky top-0 flex flex-col rounded-lg transition-colors duration-300">
                        <div className="flex flex-row-reverse">
                            <button
                                type="button"
                                onClick={onToggleModal}
                                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-3 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-300"
                                data-modal-hide="authentication-modal"
                            >
                                <svg aria-hidden="true" className="w-7 h-7 text-off-black dark:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                <span className="sr-only">{t("Close Modal")}</span>
                            </button>
                            <label className="p-4 text-medium-gray dark:text-neutral-gray-400 font-semibold transition-colors duration-300">{t("Destination")}</label>
                        </div>
                        <div className="flex flex-col my-1 px-4 w-full shadow-md bg-white dark:bg-dark-gray transition-colors duration-300">
                            {/* destination name */}
                            <h1 className="text-4xl font-semibold text-off-black dark:text-white transition-colors duration-300">{destinationTitle}</h1>
                            {/* menu tabs */}
                            <div className={pageLoaded ? "w-full flex flex-row font-semibold text-medium-gray dark:text-neutral-gray-400 visible transition-colors duration-300" : "invisible"}>
                                <label className={(destSubPage === "details") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-dark-gray dark:border-l-dark-gray dark:border-r-dark-gray border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-dark-gray mr-3 py-3 cursor-pointer transition-colors duration-300"}
                                    onClick={() => onSelectSubPage("details")}>
                                    {t("Connections")}
                                </label>
                                <label className={(destSubPage === "configuration") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-dark-gray dark:border-l-dark-gray dark:border-r-dark-gray border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-dark-gray mr-3 py-3 cursor-pointer transition-colors duration-300"}
                                    onClick={() => onSelectSubPage("configuration")}>
                                    {t("Configuration")}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* scrollable content */}
                    <div className="flex flex-col overflow-y-auto flex-grow w-full rounded-lg scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600">
                        {!pageLoaded ?
                            <div className="flex justify-center items-center h-full dark:bg-dark-gray transition-colors duration-300">
                                <Spinner />
                            </div>
                            :
                            <div className="px-4 py-3 h-full dark:bg-dark-gray transition-colors duration-300"> {/* Make content take full height */}
                                {(destSubPage === "details") && <DestinationDetailsModalTable
                                    connections={connections}
                                    requestSort={requestSort}
                                    loadingConnections={loadingConnections}
                                    onPageClicked={onPageClicked}
                                    totalCount={totalCount}
                                    pageLoaded={pageLoaded}
                                    take={take}
                                    offset={offset}
                                />}
                                {(destSubPage === "configuration") && <DestinationDetailsModalConfig
                                    currentMemberIsAdmin={currentMemberIsAdmin}
                                    destination={destination}
                                    organizations={organizations}
                                    loadMoreOrgs={loadMoreOrgs}
                                    orgInputChange={orgInputChange}
                                    onChange={onChangeSelectedDestination}
                                    onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
                                    onChangeSelectedDestinationConfig={onChangeSelectedDestinationConfig}
                                    onTestAction={onTestAction}
                                    testActionLoading={testActionLoading}
                                    testActionResponse={testActionResponse}
                                    testActionStatus={testActionStatus}
                                    onSave={onSave}
                                    onToggleModal={onToggleModal}
                                    errors={errors}
                                    rsjfErrors={rsjfErrors}
                                />}
                            </div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationDetailsModal;